"use client";

import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import gsap, { Power4 } from "gsap";
import { useGSAP } from "@gsap/react";
import ScrollTrigger from "gsap/dist/ScrollTrigger";

import rotatingText from "@/app/assets/effective-circular-icon.svg";
import Traditional360Icon from "@/app/assets/traditional-360-icon.svg";
import discoverCircular from "@/app/assets/discover-circular.svg";
import purpleArrowLeft from "@/app/assets/purple-arrow-down-left.svg";
import purpleArrowRight from "@/app/assets/purple-arrow-down-right.svg";
import ourDnaIcon from "@/app/assets/our-dna-icon.svg";
import faqsCircular from "@/app/assets/faqs-circular.svg";
import ourWorkCircular from "@/app/assets/our-work-circular.svg";
import chapter1Circular from "@/app/assets/chapter-1-icon-circular.svg";
import chapter2Circular from "@/app/assets/chapter-2-icon-circular.svg";
import chapter4Circular from "@/app/assets/chapter-4-icon-circular.svg";
import chapter5Circular from "@/app/assets/chapter-5-icon-circular.svg";
import conclusionCircular from "@/app/assets/conclusion-icon-circular.svg";

gsap.registerPlugin(ScrollTrigger);

type ArrowProps = {
    direction: string;
    className: React.ReactNode;
    wrapText?: string;
};

export default function FloatingArrow({
    direction,
    className,
    wrapText,
}: ArrowProps) {
    const arrowWrapRef = useRef(null);
    const arrowRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    let imageSrc = null;
    // assign icon based on wrapText prop
    switch (wrapText) {
        case "discover":
            imageSrc = discoverCircular;
            break;
        case "traditional-360":
            imageSrc = Traditional360Icon;
            break;
        case "effective-circular":
            imageSrc = rotatingText;
            break;
        case "our-dna":
            imageSrc = ourDnaIcon;
            break;
        case "faqs":
            imageSrc = faqsCircular;
            break;
        case "our-work":
            imageSrc = ourWorkCircular;
            break;
        case "chapter-1":
            imageSrc = chapter1Circular;
            break;
        case "chapter-2":
            imageSrc = chapter2Circular;
            break;
        case "chapter-4":
            imageSrc = chapter4Circular;
            break;
        case "chapter-5":
            imageSrc = chapter5Circular;
            break;
        case "conclusion":
            imageSrc = conclusionCircular;
            break;
        default:
            imageSrc = rotatingText;
            break;
    }

    useGSAP(() => {
        if (!isMounted) return;

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: arrowRef.current,
                    start: "top 75%",
                    end: "+=450",
                    toggleActions: "play reverse play reverse",
                    scrub: 1.05,
                },
            });

            const Arrowtl = gsap.timeline({
                scrollTrigger: {
                    trigger: arrowWrapRef.current,
                    start: "top 75%",
                    end: "+=450",
                    toggleActions: "play reverse play reverse",
                    scrub: 1,
                },
            });

            if (window.screen.width > 767) {
                Arrowtl.to(arrowWrapRef.current, { top: "-6rem" });
            } else {
                Arrowtl.to(arrowWrapRef.current, { top: "-5rem" });
            }

            if (direction === "left" || direction === "right") {
                tl.to(arrowRef.current, {
                    rotate: 0,
                    transformOrigin: "center",
                });
            }
        }, arrowWrapRef);

        return () => ctx.revert();
    }, [direction, isMounted]);

    const arrowImg = direction === "left" ? purpleArrowLeft : purpleArrowRight;

    return (
        <div
            className={`absolute duration-75 !z-20 ${className} bg-white rounded-full`}
            ref={arrowWrapRef}
        >
            <div className="relative z-[2] rounded-full flex w-[8.333vw] min-w-24 aspect-square justify-center items-center">
                <Image
                    src={imageSrc}
                    width={260}
                    height={260}
                    sizes="(min-width: 1024px) 8.333vw, 96px"
                    alt="rotating img"
                    className="absolute top-0 bottom-0 left-0 right-0 m-auto w-full h-full object-contain p-[5%] animate-rotate origin-center"
                    loading="lazy"
                />
                <Image
                    src={arrowImg}
                    width={120}
                    height={120}
                    sizes="(min-width: 1024px) 3.333vw, 38px"
                    alt="arrow img"
                    className={`w-[40%] m-auto h-auto aspect-square object-contain origin-center ${
                        direction === "left" ? "-rotate-90" : "rotate-90"
                    }`}
                    ref={arrowRef}
                    loading="lazy"
                />
            </div>
        </div>
    );
}
